import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

export const differentPasswordValidator = (text: string, compareText: string) => (control: AbstractControl) => {
  const pass: AbstractControl = control.get(text) as AbstractControl;
  const _pass: AbstractControl = control.get(compareText) as AbstractControl;
  const equal: boolean = pass.value === _pass.value;

  if (pass.dirty && pass.valid) {
    _pass.enable({ onlySelf: true, emitEvent: false });
  }

  if (!equal && _pass.dirty && _pass.value) {
    _pass.setErrors({ password: 'different password' }, { emitEvent: false });
  }

  return equal ? null : { password: 'different password' };
};

//
/**
 * @todo - zgodnie ze standardem RFC 5322 and 6854, email moze zawierać:
 * ~ duże litery w identyfikatorze użytkownika oraz nazwie domeny
 * ~ do 64 znaków - identyfikator użytkownika
 * ~ do 254 znaków - nazwa domeny (u ans 250 znaków + max 4 znaki na Top-level domain)
 */
export const EMAIL_PATTERN = '^[a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,250}\\.[a-z]{2,}$';

export const ZIP_CODE_PATTERN = /^[a-z0-9][a-z0-9- ]{0,10}[a-z0-9]$/im;

export const URL_PATTERN =
  '^' +
  // protocol identifier (optional)
  // short syntax // still required
  '(?:(?:(?:https?|ftp):)?\\/\\/)' +
  // user:pass BasicAuth (optional)
  '(?:\\S+(?::\\S*)?@)?' +
  '(?:' +
  // IP address exclusion
  // private & local networks
  '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
  '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
  '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
  // IP address dotted notation octets
  // excludes loopback network 0.0.0.0
  // excludes reserved space >= 224.0.0.0
  // excludes network & broadcast addresses
  // (first & last IP address of each class)
  '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
  '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
  '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
  '|' +
  // host & domain names, may end with dot
  // can be replaced by a shortest alternative
  // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
  '(?:' +
  '(?:' +
  '[a-z0-9\\u00a1-\\uffff]' +
  '[a-z0-9\\u00a1-\\uffff_-]{0,62}' +
  ')?' +
  '[a-z0-9\\u00a1-\\uffff]\\.' +
  ')+' +
  // TLD identifier name, may end with dot
  '(?:[a-z\\u00a1-\\uffff]{2,}\\.?)' +
  ')' +
  // port number (optional)
  '(?::\\d{2,5})?' +
  // resource path (optional)
  '(?:[/?#]\\S*)?' +
  '$';

export const minLengthArray = (minimalLength: number) => {
  return (control: AbstractControl) => {
    if (control.value.length >= minimalLength) return null;
    return { MinLengthArray: true };
  };
};

export const isEqualWord = (word: string) => {
  return (control: AbstractControl) => {
    if (control.value === word) return null;
    return { wordIsNotEqual: true };
  };
};

export const atLeastOne =
  (validator: ValidatorFn, controls: string[] | null = null): any =>
  (group: UntypedFormGroup): ValidationErrors | null => {
    const fieldList = group.controls;
    let oneOrMoreTrue = false;
    // let firstControlName: string;
    Object.keys(fieldList).forEach((field) => {
      if ((fieldList[field] as UntypedFormControl).value) {
        oneOrMoreTrue = true;
      }
    });
    return oneOrMoreTrue ? null : { oneOrMore: true };
  };

/**
 * Rejects values that are empty or whitespace only, but unlike `noWhiteSpace` tolerates leading / trailing
 * spaces (for free text that is trimmed on submit, e.g. chat messages).
 */
export const notBlank: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  typeof control.value === 'string' && control.value.trim().length === 0 ? { blank: true } : null;

export const noWhiteSpace = (control: UntypedFormControl) => {
  if (control.value?.startsWith(' ')) {
    return {
      whiteSpaceError: { value: 'control has leading whitespace' }
    };
  }
  if (control.value?.endsWith(' ')) {
    return {
      whiteSpaceError: { value: 'control has trailing whitespace' }
    };
  }
  return null;
};

export function checkCountryPattern(pattern: RegExp): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === '' && !control.hasValidator(Validators.required)) return null;
    const result = pattern.test(control.value);
    if (!result) {
      return {
        vatNumberRegexp: true
      };
    }
    return result ? null : control.errors;
  };
}

/** Normalizes a name for uniqueness comparison: trims, collapses whitespace and ignores letter case. */
export const normalizeName = (name: string): string => name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

/**
 * Flags the control with `nameExists` when its value matches one of `existingNames`,
 * ignoring letter case and leading/trailing/repeated whitespace.
 */
export function uniqueNameValidator(existingNames: readonly string[]): ValidatorFn {
  const normalizedNames = new Set(existingNames.map(normalizeName));

  return (control: AbstractControl): ValidationErrors | null =>
    normalizedNames.has(normalizeName(control.value ?? '')) ? { nameExists: true } : null;
}

/** Error key set by {@link dateNotBeforeValidator} on the group and on the end control. */
export const DATE_BEFORE_START_ERROR = 'dateBeforeStart';

function parseDateValue(value: unknown): number | null {
  if (!value) return null;
  const time = new Date(value as string | number | Date).getTime();
  return Number.isNaN(time) ? null : time;
}

function withoutError(errors: ValidationErrors | null, key: string): ValidationErrors | null {
  if (!errors || !(key in errors)) return errors;
  const { [key]: _removed, ...rest } = errors;
  return Object.keys(rest).length ? rest : null;
}

/**
 * Group validator: flags `dateBeforeStart` on the group and on the `endControlName` control
 * when its date is earlier than the `startControlName` date. Empty or unparsable values pass.
 */
export function dateNotBeforeValidator(startControlName: string, endControlName: string): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const endControl = group.get(endControlName);
    const start = parseDateValue(group.get(startControlName)?.value);
    const end = parseDateValue(endControl?.value);
    const invalid = start !== null && end !== null && end < start;

    if (endControl) {
      const errors = invalid
        ? { ...endControl.errors, [DATE_BEFORE_START_ERROR]: true }
        : withoutError(endControl.errors, DATE_BEFORE_START_ERROR);
      if (JSON.stringify(errors) !== JSON.stringify(endControl.errors)) {
        endControl.setErrors(errors, { emitEvent: false });
      }
    }

    return invalid ? { [DATE_BEFORE_START_ERROR]: true } : null;
  };
}
